import { useState, useEffect, useCallback, useRef } from 'react';
import { PaymentApiService, PaymentData } from '../services/paymentApi';

interface UsePaymentResult {
  paymentData: PaymentData | null;
  loading: boolean;
  error: string | null;
  confirming: boolean;
  timeRemaining: number;
  confirmPayment: (ref?: string) => Promise<void>;
  reFetchPayment: () => Promise<void>;
  requiresConfirmRef: boolean;
}

export const usePayment = (paymentId: string | null): UsePaymentResult => {
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef(true);
  const shouldPollRef = useRef(true);

  const fetchPayment = useCallback(async (showLoading = false) => {
    if (!paymentId) return;

    if (showLoading) setLoading(true);

    try {
      setError(null);
      const response = await PaymentApiService.getPayment(paymentId);

      if (response && response.id) {
        setPaymentData(response);
        shouldPollRef.current = response.status === 'pending';
      } else {
        setError('Failed to fetch payment data');
        shouldPollRef.current = false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      shouldPollRef.current = false;
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [paymentId]);

  const reFetchPayment = useCallback(async () => {
    await fetchPayment(true);
  }, [fetchPayment]);

  const confirmPayment = useCallback(async (ref?: string) => {
    if (!paymentId || confirming) return;

    setConfirming(true);
    try {
      const response = await PaymentApiService.confirmPayment(paymentId, ref);

      if (response && response.id) {
        await fetchPayment(false);
      } else {
        setError('Failed to confirm payment');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setConfirming(false);
    }
  }, [paymentId, confirming, fetchPayment]);

  const calculateTimeRemaining = useCallback(() => {
    if (!paymentData?.expires_at) return 0;

    const expirationTime = new Date(paymentData.expires_at).getTime();
    const now = new Date().getTime();
    const remaining = Math.max(0, expirationTime - now);

    return Math.floor(remaining / 1000);
  }, [paymentData?.expires_at]);

  useEffect(() => {
    if (!paymentId) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (isInitialLoadRef.current) {
      fetchPayment(true);
      isInitialLoadRef.current = false;
    }

    intervalRef.current = setInterval(() => {
      if (shouldPollRef.current) {
        fetchPayment(false);
      }
    }, 1000 * 5);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [paymentId]);

  useEffect(() => {
    const timerInterval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining());
    }, 1000);

    setTimeRemaining(calculateTimeRemaining());

    return () => clearInterval(timerInterval);
  }, [calculateTimeRemaining]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const requiresConfirmRef = paymentData?.details?.requires_confirm_ref ?? false;

  return {
    paymentData,
    loading,
    error,
    confirming,
    timeRemaining,
    confirmPayment,
    reFetchPayment,
    requiresConfirmRef,
  };
};